import { t } from "@lingui/macro";
import {
  BrandOrderByColumnEnum,
  BrandsWhereColumn,
  type GetBrandsQuery,
  type GetBrandsQueryVariables,
  SortOrder,
  SqlOperator,
} from "@src/__generated__/graphql";
import {
  commonQueryVariables,
  commonSearchParams,
  type TRefetch,
} from "@src/utils/apolloHelpers";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { orderBy } from "lodash";
import { computed, makeObservable, observable } from "mobx";
import { BrandModel, ClientModel } from "./models";

export enum QuickFilters {
  All = "all",
  Active = "active",
  Archived = "archived",
}

const QuickFilterDefault = QuickFilters.Active;

/**
 * store holding projects page related data
 */
export class StoreBrands {
  refetch?: TRefetch<GetBrandsQueryVariables, GetBrandsQuery>;
  pagination = new PaginationState("brands");

  @observable searchTerm = "";

  orderBy = new OrderBy<BrandOrderByColumnEnum>([
    { column: BrandOrderByColumnEnum.BrandName, order: SortOrder.Asc },
  ]);

  where = new Filters<BrandsWhereColumn>(
    [
      new Filter({
        column: BrandsWhereColumn.Status,
        hidden: true,
        operator: SqlOperator.Eq,
        title: "",
        options: [],
        value: QuickFilterDefault,
        skipWhenValueEquals: QuickFilters.All,
      }),
      new Filter({
        column: BrandsWhereColumn.ClientId,
        operator: SqlOperator.In,
        title: t`Client`,
        options: [],
      }),
      new Filter({
        column: BrandsWhereColumn.ContactId,
        operator: SqlOperator.In,
        title: t`Contact person`,
        options: [],
      }),
    ],
    {
      onChange: this.pagination.resetPage,
      tableKey: "brands",
      orderBy: this.orderBy,
    },
  );

  @observable.ref brands: BrandModel[] = [];

  @observable.ref allBrandIds: BrandModel["id"][] = [];

  @observable.ref clients: ClientModel[] = [];

  constructor() {
    makeObservable(this);
  }

  fromGetBrandsQuery(data: GetBrandsQuery) {
    this.brands =
      data.brands?.data.map((i) => {
        const {
          id,
          stats,
          name,
          code,
          image,
          archived_at,
          client,
          contacts,
          creditAccounts,
          defaultBillingEntity,
          pipedriveCustomFieldValues,
        } = i;

        return new BrandModel({
          id,
          stats,
          name,
          code,
          archived_at,
          image,
          client,
          contacts,
          creditAccounts: creditAccounts ?? [],
          defaultBillingEntityId: defaultBillingEntity?.id ?? null,
          pipedriveCustomFieldValues: pipedriveCustomFieldValues ?? [],
        });
      }) ?? [];

    this.clients = data.clientsSimpleMap?.map((i) => new ClientModel(i)) ?? [];
    if (data.brands) {
      this.pagination.setFromPaginatorInfo(data.brands.paginatorInfo);
    }
    this.allBrandIds = data.brandsSummary.brandIds;
    this.where.filtersByColumn.get(BrandsWhereColumn.ClientId)?.setOptions(
      data.clientsSimpleMap.map((i) => ({
        value: i?.id,
        label: i?.name,
      })),
    );
    this.where.filtersByColumn.get(BrandsWhereColumn.ContactId)?.setOptions(
      data.contacts.map((i) => ({
        value: i?.id,
        label: `${i?.first_name} ${i?.last_name}`,
      })),
    );
  }

  @computed get activeQuickFilter() {
    return this.where.filtersByColumn.get(BrandsWhereColumn.Status);
  }

  @computed get searchParams() {
    return commonSearchParams(this);
  }

  @computed get queryParams() {
    return commonQueryVariables(this);
  }

  static create() {
    return new StoreBrands();
  }

  @computed get clientOptions() {
    return orderBy(
      this.clients.map((i) => ({ label: i.name, value: i.id })),
      ["label"],
      ["asc"],
    );
  }
}
